import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  Popover,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material'
import { Search, Facebook, Person, FilterList } from '@mui/icons-material'
import { tags } from '../../../constants/chat'
import { IPaginationResponse } from '../../../types/pagination'
import {
  EFacebookChatRoomStatus,
  FacebookChatRoomStatus,
  IFacebookChatRoom,
  IFacebookPage,
} from '../../../types/facebook'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import {
  setChatRooms,
  setChatRoomsMutate,
} from '../../../../store/reducers/facebook/facebook-chat.slice'
import SearchTags from './SearchTags'
import useAxios from '../../../hooks/useAxios'
import { FiEdit3, FiPenTool } from 'react-icons/fi'
import { isEmpty } from 'radash'
import ActorPopover from './ChatActorPopup'
import { Tag } from '../../../components/Tag'
import { useUser } from '../../../hooks/useUser'
import { isRoleMerchant } from '../../../utils/getRoleUser'
import { message } from 'antd'

interface ChatPanelProps {
  chatRooms?: IPaginationResponse<IFacebookChatRoom>
  setParams?: (data: any) => void
  setOpenChat?: (chatRoom: IFacebookChatRoom) => void
  tabParams?: Record<string, any>
}

interface ChatListProps {
  chatRooms?: IPaginationResponse<IFacebookChatRoom>
  editableToggle?: boolean
  setSelectedChatId?: (chatId: string[]) => void
  handleOpenChatWindow?: (chatRoom: IFacebookChatRoom) => void
}

interface ChatItemProps {
  id: string
  avatarSrc: string
  name: string
  time: string
  message: string
  readBy?: string
  unReadCount?: number
  onClick?: () => void
  editableToggle?: boolean
  assignToUser?: string
  pageName?: string
  setSelectedChatId?: (chatId: any) => void
}

const ChatPanel = (props: ChatPanelProps) => {
  const { setOpenChat, tabParams } = props

  const { user } = useUser(false)

  const roles = user?.roles || []
  const isMerchant = isRoleMerchant(roles)

  const dispatch = useDispatch()
  const [search, setSearch] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [editableToggle, setEditableToggle] = useState<boolean>(false)
  const [selectedChatId, setSelectedChatId] = useState<string[]>([])
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search)
  const [params, setParams] = useState<Record<string, any>>({
    page: 1,
    perPage: 10,
    status: EFacebookChatRoomStatus.OPEN,
    search: '',
    chatType: 'me',
  })
  const [tabStatus, setTabStatus] = useState(0)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [popoverUserAnchor, setPopoverUserAnchor] =
    useState<HTMLElement | null>(null)

  const [availableAssignUsers, setAvailableAssignUsers] = useState<string[]>([])

  const {
    data: chatRooms,
    isLoading,
    mutate,
  } = useAxios<IPaginationResponse<IFacebookChatRoom>>(
    {
      url: '/facebook/pages/chatrooms',
      method: 'GET',
      params: {
        ...params,
        search: debouncedSearch,
      },
    },
    {
      revalidateOnFocus: true,
    },
  )

  useEffect(() => {
    setParams((prevState) => ({
      ...prevState,
      ...tabParams,
    }))
  }, [tabParams])

  useEffect(() => {
    if (chatRooms?.records) {
      dispatch(setChatRooms({ chatRooms }))
      dispatch(setChatRoomsMutate({ mutate }))
    }
  }, [dispatch, chatRooms])

  useEffect(() => {
    setLoading(true)
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setLoading(false)
    }, 1000)

    return () => {
      clearTimeout(handler)
      setLoading(false)
    }
  }, [search])

  const currentLoadingState = loading || isLoading

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabStatus(newValue)
    setParams((prevState) => ({
      ...prevState,
      page: 1,
      status: FacebookChatRoomStatus[newValue].status,
    }))
  }

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget)
  }

  const handleAssignUserClick = (event: React.MouseEvent<HTMLElement>) => {
    const pageIds = chatRooms?.records
      .filter((chatRoom) => selectedChatId.includes(chatRoom.objectId))
      .map((chatRoom) => chatRoom.pageId)

    // pageId unique
    const uniquePageIds = Array.from(new Set(pageIds))
    if (uniquePageIds.length > 1) {
      setPopoverUserAnchor(null)
      message.error('ไม่สามารถกำหนดผู้ดูแลห้องแชทของหลายเพจได้')
      return
    }

    const chatRoom = chatRooms?.records.find(
      (chatRoom) => chatRoom.pageId === uniquePageIds[0],
    )

    //console.log('chatRoom', chatRoom)

    const assigns = chatRoom?.page?.assigns || []
    if (isEmpty(assigns)) {
      setPopoverUserAnchor(null)
      message.error('ไม่พบผู้ดูแลห้องแชท กรุณากำหนดที่เมนูเชื่อมต่อแชท')
      return
    }

    setAvailableAssignUsers(assigns)
    setPopoverUserAnchor(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setPopoverAnchor(null)
    setSelectedChatId([])
    setEditableToggle(false)
    mutate()
  }

  const handlePopoverUserClose = () => {
    setPopoverUserAnchor(null)
    setSelectedChatId([])
    setEditableToggle(false)
    mutate()
  }

  const isPopoverOpen = Boolean(popoverAnchor)
  const isPopoverUserOpen = Boolean(popoverUserAnchor)

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Header onFilterClick={handleFilterClick} />
      <Box sx={{ px: 4, py: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="ค้นหา"
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <Search fontSize="small" sx={{ color: 'grey.500', ml: 1 }} />
            ),
            sx: { pl: 2, borderRadius: 3, mb: 1 },
          }}
        />
        {isMerchant && (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Button
              variant="outlined"
              size="small"
              color={'info'}
              onClick={() => {
                setEditableToggle(!editableToggle)
                setSelectedChatId([])
              }}
              startIcon={<FiEdit3 />}
            >
              เลือกห้อง
            </Button>
            {!isEmpty(selectedChatId) && editableToggle && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={handleAssignUserClick}
                >
                  กำหนดผู้ดูแล
                </Button>
                <ActorPopover
                  availableAssignUsers={availableAssignUsers}
                  ids={selectedChatId}
                  anchorEl={popoverUserAnchor}
                  isOpen={isPopoverUserOpen}
                  onClose={handlePopoverUserClose}
                />
              </>
            )}
          </Box>
        )}
      </Box>
      <TabStatus tabStatus={tabStatus} handleTabChange={handleTabChange} />
      <Divider />
      {currentLoadingState ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexGrow: 1,
            py: 2,
          }}
        >
          <Typography variant="body1">กำลังโหลด...</Typography>
        </Box>
      ) : (
        <ChatList
          setSelectedChatId={setSelectedChatId}
          editableToggle={editableToggle}
          chatRooms={chatRooms}
          handleOpenChatWindow={setOpenChat}
        />
      )}
      {/* <ChatList chatRooms={chatRooms} handleOpenChatWindow={setOpenChat} /> */}
      <FilterPopover
        anchorEl={popoverAnchor}
        isOpen={isPopoverOpen}
        onClose={handlePopoverClose}
        setParams={setParams}
        params={params}
      />
    </Box>
  )
}

const Header: React.FC<{
  onFilterClick: (event: React.MouseEvent<HTMLElement>) => void
}> = ({ onFilterClick }) => (
  <Box>
    <Box
      p={2}
      borderBottom={1}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderColor="grey.200"
    >
      <Typography variant="subtitle1" fontWeight="bold">
        กล่องข้อความ
      </Typography>
      <Button
        variant="outlined"
        startIcon={<FilterList />}
        sx={{ borderRadius: 2 }}
        onClick={onFilterClick}
      >
        ฟิลเตอร์
      </Button>
    </Box>
    {/* <Box display="flex" justifyContent="space-between" alignItems="center"> */}
    {/* <Typography variant="h6" fontWeight="bold">
        แชททั้งหมด
      </Typography> */}
    {/* </Box> */}
  </Box>
)

const ChatList = (props: ChatListProps) => {
  const { chatRooms, handleOpenChatWindow, editableToggle, setSelectedChatId } =
    props
  return (
    <Box sx={{ overflowY: 'auto', flexGrow: 1, height: 500 }}>
      {chatRooms?.records.map((chatRoom, index) => (
        <List disablePadding key={index} sx={{ borderRadius: 0 }}>
          <ChatListItem
            setSelectedChatId={setSelectedChatId}
            editableToggle={editableToggle}
            id={chatRoom.objectId}
            readBy={chatRoom?.readBy}
            avatarSrc={chatRoom.imageUrl ?? ''}
            name={chatRoom.name}
            assignToUser={chatRoom.assignToUser}
            time={dayjs(chatRoom.updatedAt).format('YYYY-MM-DD HH:mm')}
            message={chatRoom.message}
            onClick={() => handleOpenChatWindow?.(chatRoom)}
            unReadCount={chatRoom?.unReadCount ?? 0}
            pageName={chatRoom.pageName}
          />
        </List>
      ))}
    </Box>
  )
}
const TabStatus: React.FC<{
  tabStatus: number
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void
}> = ({ tabStatus, handleTabChange }) => (
  <Box sx={{ px: 4 }}>
    <Tabs
      value={tabStatus}
      onChange={handleTabChange}
      variant="fullWidth"
      textColor="primary"
      indicatorColor="primary"
    >
      {FacebookChatRoomStatus.map((status) => (
        <Tab key={status.status} label={status.label} />
      ))}
    </Tabs>
  </Box>
)

const ChatListItem = (props: ChatItemProps) => {
  const {
    id,
    avatarSrc,
    name,
    time,
    message,
    onClick,
    readBy = null,
    unReadCount = 0,
    editableToggle,
    setSelectedChatId,
    assignToUser,
    pageName,
  } = props
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSelectedChatId?.((prevState: string[]) => {
      if (prevState.includes(value)) {
        return prevState.filter((id) => id !== value)
      }
      return [...prevState, value]
    })
  }
  return (
    <ListItem
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        borderBottom: 1,
        borderColor: 'grey.200',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        borderRadius: 0,
        '&:hover': { backgroundColor: 'grey.100' },
      }}
    >
      {editableToggle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
          <Checkbox value={id} onChange={(e) => handleOnChange(e)} />
        </Box>
      )}
      <Box sx={{ position: 'relative', mr: 2 }} onClick={onClick}>
        <Avatar src={avatarSrc} alt="avatar" sx={{ width: 55, height: 55 }} />
        <Avatar
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            bgcolor: 'blue.600',
            width: 24,
            height: 24,
          }}
        >
          <Facebook sx={{ fontSize: 16 }} />
        </Avatar>
      </Box>
      <Box flex="1" minWidth="0" onClick={onClick}>
        <Box
          display={'flex'}
          alignItems={'center'}
          gap={1}
          justifyContent={'space-between'}
        >
          <Typography variant="body1" fontWeight="bold">
            {name}
          </Typography>
          <Tag color={assignToUser ? 'primary' : 'error'}>
            {assignToUser || 'ยังไม่กำหนด'}
          </Tag>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {time}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={!readBy ? '600' : '300'}
          color={!readBy ? '#000' : 'text.secondary'}
          noWrap
        >
          {message}
        </Typography>
        <Typography variant="body2" fontWeight={500} fontSize={11} noWrap>
          {pageName}
        </Typography>
      </Box>
      <Box minWidth="30">
        {unReadCount > 0 && (
          <Box
            sx={{
              ml: 'auto',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              color: 'white',
            }}
            bgcolor={'error.main'}
          >
            {unReadCount}
          </Box>
        )}
      </Box>
    </ListItem>
  )
}

interface FilterPopoverProps {
  anchorEl: HTMLElement | null
  isOpen: boolean
  onClose: () => void
  setParams: (params: Record<string, any>) => void
  params: Record<string, any>
}

/*Disabled*/
const FilterPopover: React.FC<FilterPopoverProps> = ({
  anchorEl,
  isOpen,
  onClose,
  setParams,
  params,
}) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [pageIds, setPageIds] = useState<string[]>([])

  useEffect(() => {
    setPageIds(params.pageIds || [])
  }, [params.pageIds])

  const { data, mutate } = useAxios<IFacebookPage[]>(
    {
      url: '/facebook/pages/assign',
      method: 'GET',
    },
    {
      revalidateOnFocus: false,
    },
  )

  const handleSelectPages = (pageId: string) => {
    const index = pageIds.indexOf(pageId)
    const newPageIds = [...pageIds]
    if (index > -1) {
      newPageIds.splice(index, 1)
    } else {
      newPageIds.push(pageId)
    }
    setPageIds(newPageIds)
  }

  const handleOnSubmit = () => {
    setParams((prevState: any) => ({
      ...prevState,
      pageIds,
    }))
    onClose()
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box sx={{ width: 400, p: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="เลือกเพจ" />
          {/* <Tab label="แท็ก" /> */}
          {/* <Tab label="ช่องทาง" /> */}
        </Tabs>
        <Divider />
        <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
          <Box display={selectedTab === 0 ? 'block' : 'none'}>
            <Typography variant="subtitle1" gutterBottom>
              รายชื่อเพจ
            </Typography>
            <FormGroup>
              {data?.map((page) => (
                <FormControlLabel
                  key={page.pageId}
                  control={
                    <Checkbox
                      checked={pageIds.includes(page.pageId)}
                      onChange={() => handleSelectPages(page.pageId)}
                    />
                  }
                  label={page.pageName}
                />
              ))}
            </FormGroup>
            {/* <FormControlLabel control={<Checkbox />} label="อ่านแล้ว" />
            <FormControlLabel control={<Checkbox />} label="ยังไม่อ่าน" /> */}
            {/* <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              สถานะการตอบ
            </Typography>
            <FormControlLabel control={<Checkbox />} label="ยังไม่ได้ตอบ" />
            <FormControlLabel control={<Checkbox />} label="ตอบแล้ว" /> */}
          </Box>

          <Box display={selectedTab === 1 ? 'block' : 'none'}>
            <SearchTags />
          </Box>
        </Box>
        <Divider />
        <Box display="flex" justifyContent="flex-end" p={2}>
          <Button variant="contained" onClick={() => handleOnSubmit()}>
            ใช้งาน
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

export default ChatPanel
